import { useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { AuthSplitLayout, AuthImagePanel, Button, Input, Textarea, normalizeWebsiteHost } from '@discovr/ui';
import { Hourglass, CheckCircle } from '@discovr/ui/design-system/icons';
import { waitlist } from '@discovr/api';

// Figma "Screen / Join Waitlist". Brand onboarding is waitlist-based, so this is
// effectively the signup page. Fields map 1:1 to waitlist.joinBrand; the Figma
// "Phone Number" field is omitted (the BE has no phone field).

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type EmailStatus = 'idle' | 'checking' | 'valid' | 'invalid';

const SIDE_PANEL = <AuthImagePanel />;

// Figma marks required fields with a red asterisk (#EF4444).
const Req = () => <span className="text-[#EF4444]">&nbsp;*</span>;

const FOOTER = (
  <>
    <p className="text-center text-body-sm-regular text-foreground">
      Already have an account?{' '}
      <Link to="/login" className="text-body-sm-medium text-[#6c09f7] hover:underline">
        Login
      </Link>
    </p>
    <p className="text-center text-body-sm-regular text-foreground">
      Looking to join a team already on Discovr?{' '}
      <Link to="/join" className="text-body-sm-medium text-(--brand-500) hover:underline">
        Request access
      </Link>
    </p>
    <p className="text-center text-caption-regular text-muted-foreground">
      By signing up, you agree to our{' '}
      <Link to="/terms" className="text-stone-700 hover:text-foreground">Terms</Link>
      {' '}and{' '}
      <Link to="/privacy-policy" className="text-stone-700 hover:text-foreground">Privacy Policy</Link>
    </p>
  </>
);

export default function WaitlistPage() {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    workEmail: '',
    company: '',
    website: '',
    message: '',
  });
  const [emailStatus, setEmailStatus] = useState<EmailStatus>('idle');
  const [emailError, setEmailError] = useState('');
  const [websiteError, setWebsiteError] = useState('');
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const emailAbort = useRef<AbortController | null>(null);

  const set = (patch: Partial<typeof form>) => setForm((p) => ({ ...p, ...patch }));

  // Format check, then a server sendability check. Fail-open on a transient
  // failure — the BE re-validates on submit as the real gate.
  const runEmailCheck = async (raw: string): Promise<boolean> => {
    const value = raw.trim();
    if (!value) {
      setEmailError('Work email is required');
      setEmailStatus('invalid');
      return false;
    }
    if (!EMAIL_RE.test(value)) {
      setEmailError('Enter a valid email address');
      setEmailStatus('invalid');
      return false;
    }
    emailAbort.current?.abort();
    const controller = new AbortController();
    emailAbort.current = controller;
    setEmailStatus('checking');
    setEmailError('');
    try {
      const res = await waitlist.validateEmail(value, controller.signal);
      if (res.valid) {
        setEmailStatus('valid');
        return true;
      }
      setEmailStatus('invalid');
      setEmailError('Enter a valid work email address');
      return false;
    } catch {
      if (controller.signal.aborted) return false;
      setEmailStatus('valid'); // fail-open
      return true;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setFormError('First and last name are required');
      return;
    }
    if (emailStatus !== 'valid' && !(await runEmailCheck(form.workEmail))) return;
    if (!form.company.trim()) {
      setFormError('Company / organization is required');
      return;
    }
    // Accepts bare domains, www hosts, and full http(s) URLs (industry
    // standard, mirrors the BE discovr_website validator); canonicalized to
    // the hostname. Empty is fine — the field is optional.
    const website = normalizeWebsiteHost(form.website);
    if (website === null) {
      setWebsiteError('Enter a valid website (e.g. yourbrand.com or https://yourbrand.com)');
      return;
    }
    setIsSubmitting(true);
    try {
      await waitlist.joinBrand({
        email: form.workEmail.trim(),
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        company_name: form.company.trim(),
        website: website || undefined,
        message: form.message.trim() || undefined,
      });
      setSubmitted(true);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to join the waitlist. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <AuthSplitLayout
        eyebrow="You're on the list"
        eyebrowIcon={CheckCircle}
        headline="Thanks for your interest!"
        subheadline="We'll review your details and reach out soon with early-access information."
        sidePanel={SIDE_PANEL}
        footer={
          <p className="text-center text-body-sm-regular text-foreground">
            Already approved?{' '}
            <Link to="/login" className="text-body-sm-medium text-[#6c09f7] hover:underline">Login</Link>
          </p>
        }
      >
        <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-body-sm-regular text-emerald-700">
          Your request has been received — keep an eye on your inbox.
        </div>
      </AuthSplitLayout>
    );
  }

  const labelCls = 'text-body-sm-medium text-foreground';
  const fieldCls = 'h-11 rounded-lg border-border bg-muted focus:bg-card';

  return (
    <AuthSplitLayout
      eyebrow="Join the waitlist"
      eyebrowIcon={Hourglass}
      headline="Get early access to Discovr."
      subheadline="Tell us about your brand and we'll reach out when your spot opens up."
      sidePanel={SIDE_PANEL}
      footer={FOOTER}
    >
      <form onSubmit={(e) => { void handleSubmit(e); }} className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <label htmlFor="wl-first" className={labelCls}>First Name<Req /></label>
            <Input id="wl-first" placeholder="First name" className={fieldCls}
              value={form.firstName} onChange={(e) => set({ firstName: e.target.value })} required />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="wl-last" className={labelCls}>Last Name<Req /></label>
            <Input id="wl-last" placeholder="Last name" className={fieldCls}
              value={form.lastName} onChange={(e) => set({ lastName: e.target.value })} required />
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="wl-email" className={labelCls}>Work Email Address<Req /></label>
          <Input id="wl-email" type="email" placeholder="you@company.com" className={fieldCls}
            value={form.workEmail} aria-invalid={!!emailError}
            onChange={(e) => { set({ workEmail: e.target.value }); setEmailError(''); setEmailStatus('idle'); }}
            onBlur={() => { void runEmailCheck(form.workEmail); }} required />
          {emailStatus === 'checking' && <p className="text-caption-regular text-muted-foreground">Verifying…</p>}
          {emailError && <p className="text-caption-regular text-rose-600">{emailError}</p>}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <label htmlFor="wl-company" className={labelCls}>Company/Organization<Req /></label>
            <Input id="wl-company" placeholder="Your company" className={fieldCls}
              value={form.company} onChange={(e) => set({ company: e.target.value })} required />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="wl-website" className={labelCls}>Brand Website <span className="text-muted-foreground">(optional)</span></label>
            <Input id="wl-website" placeholder="yourbrand.com" className={fieldCls}
              value={form.website} aria-invalid={!!websiteError}
              onChange={(e) => { set({ website: e.target.value }); setWebsiteError(''); }} />
            {websiteError && <p className="text-caption-regular text-rose-600">{websiteError}</p>}
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="wl-message" className={labelCls}>Message <span className="text-muted-foreground">(optional)</span></label>
          <Textarea id="wl-message" rows={3} placeholder="Tell us about your brand, goals, or anything else."
            className="h-20 min-h-0 resize-none overflow-y-auto rounded-lg border-border bg-muted field-sizing-fixed focus:bg-card"
            value={form.message} onChange={(e) => set({ message: e.target.value })} />
        </div>

        {formError && (
          <div className="rounded-lg border border-rose-100 bg-rose-50 px-4 py-3 text-body-sm-regular text-rose-700">
            {formError}
          </div>
        )}

        <Button type="submit" disabled={isSubmitting || emailStatus === 'checking'}
          className="h-11 w-full rounded-lg bg-primary text-body-md-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
          {isSubmitting ? 'Submitting…' : emailStatus === 'checking' ? 'Verifying…' : 'Join Waitlist'}
        </Button>
      </form>
    </AuthSplitLayout>
  );
}
